/**
 * PictView-Retold-Shell
 *
 * The default application shell. It uses pict-section-modal's shell() to lay out a
 * fixed top bar, a resizable left sidebar (navigation), and a center workspace
 * that entity list/detail views render into. An app can ship its own shell view
 * and point shellViewHash() at it; this one gives a trivial app a working frame.
 *
 * Lifted from plansheet's PictView-Shell and generalized (brand + region ids are
 * configuration, no plansheet business chrome).
 */

const libPictView = require('pict-view');

function escapeHTML(pText)
{
	return String(pText === null || typeof pText === 'undefined' ? '' : pText)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

class PictViewRetoldShell extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._shellPanelsBuilt = false;
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap && typeof this.pict.CSSMap.injectCSS === 'function') { this.pict.CSSMap.injectCSS(); }
		if (!this._shellPanelsBuilt || (typeof document !== 'undefined' && !document.getElementById('Retold-TopBar')))
		{
			this._buildShell();
			this._shellPanelsBuilt = true;
		}
		// The panels are rebuilt above whenever they are missing (a sign-out tears them
		// down); the brand bar, which carries the signed-in indicator, is refreshed on
		// every render so signing in as a different user updates who the top bar shows.
		this._renderBrandBar();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_buildShell()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpMount = (typeof document !== 'undefined') ? document.getElementById('Retold-Shell-Mount') : null;
		if (!tmpModal || typeof tmpModal.shell !== 'function' || !tmpMount)
		{
			this.log.warn('Retold shell: modal or mount not available; shell not built.');
			return;
		}

		this._shell = tmpModal.shell(tmpMount, { PersistenceKey: (this.options.PersistenceKey || 'retold-shell') });

		this._shell.addPanel({ Hash: 'topbar', Side: 'top', Mode: 'fixed', Size: 52, ContentDestinationId: 'Retold-TopBar' });
		this._shell.addPanel(
			{
				Hash: 'sidebar', Side: 'left', Mode: 'resizable',
				Size: 260, MinSize: 180, MaxSize: 480, Title: 'Navigate',
				ContentDestinationId: 'Retold-Sidebar', ResponsiveDrawer: 820
			});
		this._shell.center({ ContentDestinationId: 'Retold-Workspace' });

		this._mountRecordSetContainer();
		this._renderBrandBar();
		this._renderNavigation();
	}

	/**
	 * Mount the two nested containers pict-section-recordset paints into, inside the
	 * shell's workspace, so recordset renders inside the app frame rather than having
	 * the module's defaults overridden from the outside.
	 *
	 * BOTH are required, and they are not the same element:
	 *   #PictRecordSetContainer -- the outer host, where the module's RecordBaseView
	 *                              and MacroView default.
	 *   #PRSP_Container         -- the inner mount the list / read / create / dashboard
	 *                              / associate views actually target.
	 *
	 * The module never creates the inner one; it is the HOST application's job (both
	 * of the module's own example apps do it themselves -- simple_entity ships it as
	 * static HTML, the bookstore app writes it in on every route). Without it every
	 * recordset view renders into a destination that does not exist and silently
	 * paints nothing, which reads as "the data never loaded" even though the query
	 * returned 200.
	 */
	_mountRecordSetContainer()
	{
		let tmpWorkspace = document.getElementById('Retold-Workspace');
		if (!tmpWorkspace) { return; }
		let tmpContainer = document.getElementById('PictRecordSetContainer');
		if (!tmpContainer)
		{
			tmpContainer = document.createElement('div');
			tmpContainer.id = 'PictRecordSetContainer';
			tmpContainer.className = 'retold-recordset-container';
			tmpWorkspace.appendChild(tmpContainer);
		}
		if (!document.getElementById('PRSP_Container'))
		{
			let tmpRecordSetMount = document.createElement('div');
			tmpRecordSetMount.id = 'PRSP_Container';
			tmpContainer.appendChild(tmpRecordSetMount);
		}
	}

	_renderBrandBar()
	{
		let tmpBar = document.getElementById('Retold-TopBar');
		if (!tmpBar) { return; }
		let tmpBrand = this.options.Brand || {};
		let tmpName = tmpBrand.Name || 'Retold Application';

		let tmpHTML = '<div class="retold-brandbar"><span class="retold-brandmark">' + escapeHTML(tmpName) + '</span>';

		// Signed-in indicator + sign-out, shown only when an auth session is live (with auth
		// off there is no session and nothing to show). logout() lives on the application
		// (pict.PictApplication, set at boot), which ends the session and renders the sign-in
		// view. Reached from the inline handler through the _Pict global.
		let tmpAuthClient = this.pict.providers['Retold-AuthClient'];
		let tmpSession = (tmpAuthClient && typeof tmpAuthClient.session === 'function') ? tmpAuthClient.session() : null;
		if (tmpSession && tmpSession.LoggedIn)
		{
			let tmpUser = tmpSession.UserRecord || {};
			let tmpWho = tmpUser.DisplayName || tmpUser.FullName || tmpUser.LoginID || 'Signed in';
			// System-administrator chip (e.g. "root admin"), shown when the session carries a SystemRole.
			let tmpAdminBadge = tmpUser.SystemRole
				? '<span class="retold-admin-badge" title="System administrator">' + escapeHTML(tmpUser.SystemRole) + ' admin</span>'
				: '';
			tmpHTML += '<div class="retold-user">'
				+ tmpAdminBadge
				+ '<span class="retold-user-name" title="Signed in">' + escapeHTML(tmpWho) + '</span>'
				+ '<button type="button" class="retold-logout" onclick="_Pict.PictApplication.logout()">Sign out</button>'
				+ '</div>';
		}

		tmpHTML += '</div>';
		tmpBar.innerHTML = tmpHTML;
	}

	// A simple sidebar built from the navigation graph. The pict-section-navigation
	// provider is still registered by the base (for the command palette and future
	// use); this default sidebar keeps a trivial app running without extra wiring.
	// True when the signed-in account carries any system role (the admin-nav gate keys off this).
	_isSystemAdmin()
	{
		let tmpAuthClient = this.pict.providers['Retold-AuthClient'];
		let tmpSession = (tmpAuthClient && typeof tmpAuthClient.session === 'function') ? tmpAuthClient.session() : null;
		return !!(tmpSession && tmpSession.UserRecord && tmpSession.UserRecord.SystemRole);
	}

	_renderNavigation()
	{
		let tmpSidebar = document.getElementById('Retold-Sidebar');
		if (!tmpSidebar) { return; }
		let tmpGraph = this.options.NavigationGraph || [];
		let tmpIsSystemAdmin = this._isSystemAdmin();
		let tmpHTML = '<nav class="retold-nav">';
		for (let i = 0; i < tmpGraph.length; i++)
		{
			let tmpNode = tmpGraph[i] || {};
			// Admin-only entries (SystemAdmin: true) are hidden unless the account is a system admin.
			if (tmpNode.SystemAdmin && !tmpIsSystemAdmin) { continue; }
			let tmpRoute = tmpNode.Route ? String(tmpNode.Route) : '';
			let tmpLabel = tmpNode.Name || tmpNode.Title || tmpRoute;
			if (!tmpRoute) { continue; }
			let tmpHref = (tmpRoute.charAt(0) === '#') ? tmpRoute : ('#' + tmpRoute);
			tmpHTML += '<a class="retold-nav-item" href="' + escapeHTML(tmpHref) + '">' + escapeHTML(tmpLabel) + '</a>';
		}
		tmpHTML += '</nav>';
		tmpSidebar.innerHTML = tmpHTML;
	}
}

module.exports = PictViewRetoldShell;
module.exports.default_configuration =
{
	ViewIdentifier: 'Retold-Application-Shell',
	DefaultRenderable: 'Retold-Application-Shell',
	DefaultDestinationAddress: '#App-Container',
	AutoRender: false,
	// Themed backgrounds are always paired with a themed foreground. #App-Container sets
	// the baseline so everything the shell hosts -- including the record-set views that
	// paint into the workspace -- inherits readable text instead of the browser default
	// black, which disappears on a dark panel. Fallbacks stay light to match the light
	// background fallbacks beside them.
	CSS: /*css*/ `
		html, body { height: 100%; margin: 0; padding: 0; }
		#App-Container { height: 100%; min-height: 0; overflow: hidden;
			background: var(--theme-color-background-primary, #f6f7f9);
			color: var(--theme-color-text-primary, #222); }
		.retold-brandbar { display: flex; align-items: center; gap: 12px; height: 100%; padding: 0 16px;
			background: var(--theme-color-background-panel, #1f2430); color: var(--theme-color-text-primary, #f5f5f5); }
		.retold-brandmark { font-weight: 600; font-size: 16px; letter-spacing: 0.02em; }
			.retold-user { margin-left: auto; display: flex; align-items: center; gap: 10px; }
			.retold-user-name { font-size: 13px; opacity: 0.85; white-space: nowrap; }
			.retold-logout { padding: 5px 12px; border-radius: 6px; font-size: 13px; cursor: pointer;
				background: transparent; color: inherit;
				border: 1px solid var(--theme-color-border-default, rgba(255,255,255,0.35)); }
			.retold-logout:hover { background: rgba(255,255,255,0.12); }
				.retold-admin-badge { font-size: 11px; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase;
					padding: 2px 8px; border-radius: 999px; white-space: nowrap;
					background: var(--theme-color-status-warning, #b8860b); color: #ffffff; }
		.retold-nav { display: flex; flex-direction: column; padding: 8px; gap: 2px; }
		.retold-nav-item { display: block; padding: 8px 12px; border-radius: 6px; text-decoration: none;
			color: var(--theme-color-text-primary, #222); }
		.retold-nav-item:hover { background: var(--theme-color-background-selected, rgba(0,0,0,0.06)); }
		.retold-recordset-container { height: 100%; overflow: auto; padding: 16px 20px;
			color: var(--theme-color-text-primary, #222); }
		.retold-entity-list { padding: 16px 20px; }
		.retold-entity-list h1 { font-size: 20px; margin: 0 0 12px 0; }
		.retold-table { width: 100%; border-collapse: collapse; font-size: 14px; }
		.retold-table th, .retold-table td { text-align: left; padding: 6px 10px; border-bottom: 1px solid var(--theme-color-border-default, #e2e2e2); }
		.retold-table th { font-weight: 600; }
		.retold-row { cursor: pointer; }
		.retold-row:hover { background: var(--theme-color-background-selected, rgba(0,0,0,0.04)); }
		.retold-list-actions { margin: 0 0 12px 0; }
		.retold-button { display: inline-block; padding: 7px 12px; border-radius: 6px; text-decoration: none; font-size: 13px;
			background: var(--theme-color-brand-primary, #3a6ea5); color: #ffffff; }
		.retold-entity-detail { padding: 16px 20px; max-width: 620px; }
		.retold-entity-detail h1 { font-size: 20px; margin: 0 0 14px 0; }
		.retold-detail-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; font-size: 13px; }
		.retold-detail-field input, .retold-detail-field textarea, .retold-detail-field select {
			padding: 8px 10px; border-radius: 6px; font-size: 14px;
			background: var(--theme-color-background-primary, #ffffff);
			color: var(--theme-color-text-primary, #222);
			border: 1px solid var(--theme-color-border-default, #d6d9de); }
		.retold-detail-field input[type="checkbox"] { width: 16px; height: 16px; padding: 0; }
		.retold-detail-actions { display: flex; align-items: center; gap: 12px; margin-top: 8px; }
		.retold-detail-actions button { padding: 8px 14px; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;
			background: var(--theme-color-brand-primary, #3a6ea5); color: #ffffff; }
		.retold-detail-status { font-size: 13px; color: var(--theme-color-text-secondary, #666); }
	`,
	Templates:
	[
		{ Hash: 'Retold-Application-Shell', Template: '<div id="Retold-Shell-Mount" style="height:100%"></div>' }
	],
	Renderables:
	[
		{ RenderableHash: 'Retold-Application-Shell', TemplateHash: 'Retold-Application-Shell', ContentDestinationAddress: '#App-Container' }
	]
};
